// Framework
import pop from './components';

// Components
import Accordion from './components/accordion';
import Carousel from './components/carousel';
import Header from './components/header';
import ImageCarousel from './components/image-carousel';
import ImageGallery from './components/image-gallery';
import LeadershipListing from './components/leadership-listing';
import LocationsMap from './components/locations-map';
import MediaPressListing from './components/media-press-listing';
import Modal from './components/modal';
import PinLocator from './components/pin-locator';
import ProjectsListing from './components/projects-listing';
import SectionContactForm from './components/section-contact-form';
import SectionContentProgession from './components/section-content-progression';
import SectionExpandingCards from './components/section-expanding-cards';
import SectionFiftyFiftySplit from './components/section-fifty-fifty-split';
import SectionHero from './components/section-hero';
import SectionMarketMap from './components/section-market-map';
import SectionProjectCarousel from './components/section-project-carousel';
import SectionScrollingCards from './components/section-scrolling-cards';
import SectionSidekick from './components/section-sidekick';

// Elements
import BackgroundGrid from './elements/BackgroundGrid';

// Helpers
import {
    getYoutubeId,
    limitLoops,
    externalLinks,
} from './utilities';
import countUp from './components/count-up';

// Define map of component handles to component classes
const classMap = {
    accordion: Accordion,
    carousel: Carousel,
    header: Header,
    'image-carousel': ImageCarousel,
    'image-gallery': ImageGallery,
    'leadership-listing': LeadershipListing,
    'locations-map': LocationsMap,
    modal: Modal,
    'media-press-listing': MediaPressListing,
    'pin-locator': PinLocator,
    'projects-listing': ProjectsListing,
    'section-contact': SectionContactForm,
    'section-content-progression': SectionContentProgession,
    'section-expanding-cards': SectionExpandingCards,
    'section-fifty-fifty-split': SectionFiftyFiftySplit,
    'section-hero': SectionHero,
    'section-market-map': SectionMarketMap,
    'section-project-carousel': SectionProjectCarousel,
    'section-scrolling-cards': SectionScrollingCards,
    'section-sidekick': SectionSidekick,
};

// Define all actions/commands that components pub/sub
const actions = {
    // Action events
    lockScroll: 'lock-scroll',
    unlockScroll: 'unlock-scroll',
    openModal: 'open-modal',
    closeModal: 'close-modal',
    loadModal: 'load-modal',
    showFieldError: 'show-field-error',
};

// Event handler functions
function handleDOMConentLoaded() {
    // Set various browser/environment classes
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
    document.documentElement.setAttribute('data-useragent', navigator.userAgent);

    function cb({ events }) {
        function handleBodyClick(e) {
            const link = e.target.closest('a');

            if (!link) return;

            // Anchors
            if (link.matches('[href="#"]')) {
                e.preventDefault();

                const target = document.querySelector(link.href);

                if (target) {
                    target.scrollIntoView({ behavior: 'smooth' });
                }
            }

            // Videos
            if (link.matches('[href*="youtube.com"]') && !link.dataset.passthrough) {
                e.preventDefault();

                const videoId = link.href.split('v=')[1];

                fetch(`/json/youtube-video?${new URLSearchParams({ videoId })}`, {
                    headers: { Accept: 'application/json' },
                })
                    .then(res => res.json().then(json => ({
                        status: res.status,
                        ...json,
                    })))
                    .then(({
                        status,
                        markup = '',
                    }) => {
                        switch (status) {
                        case 500:
                            break;
                        case 400:
                            break;
                        case 200:
                        default:
                            events.emit(actions.loadModal, { markup });
                        }
                    });
            }
        }

        document.body.addEventListener('click', handleBodyClick);

        if (window.location.hash) {
            const target = document.querySelector(window.location.hash);

            if (target) {
                target.scrollIntoView({ behavior: 'smooth' });
            }
        }

        // Only play videos when they are in view for scroll performance
        const videoObserver = new IntersectionObserver(entries => {
            entries.forEach(({ target, isIntersecting }) => {
                if (isIntersecting) {
                    target.play();
                } else {
                    target.pause();
                }
            });
        }, { threshold: 0.1 });
        Array.from(document.querySelectorAll('video'))
            .filter(v => v.hasAttribute('playsinline'))
            .forEach(v => { videoObserver.observe(v); });
    }

    pop({ classMap, actions, cb });

    document.querySelectorAll('[data-bg-grid]').forEach(el => {
        new BackgroundGrid(el); // eslint-disable-line no-new
    });

    // convert ckeditor oembeds to iframes
    document.querySelectorAll('oembed')
        .forEach(el => {
            const iframe = document.createElement('iframe');

            // youtube embed
            const youtubeId = getYoutubeId(el.getAttribute('url'));
            if (youtubeId) {
                iframe.setAttribute('src', `https://www.youtube.com/embed/${youtubeId}`);
                iframe.setAttribute('frameborder', 0);
                iframe.setAttribute('allow', 'autoplay; encrypted-media');
                iframe.setAttribute('allowfullscreen', '');

                el.parentElement.appendChild(iframe);
            }

            // ... other embed types?
        });

    // after 5 minutes, pause looping videos
    limitLoops();
    externalLinks();
    countUp();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleDOMConentLoaded);
} else {
    handleDOMConentLoaded();
}
